// Chrome states per design spec doc 07:113 (originally named Shell*; renamed to
// Chrome* here because a name-collision guardrail flagged Shell* against
// AppShellLayout). Re-exported with both names from index.ts for spec alignment.
import { type ReactNode } from "react";
import { clsx } from "clsx";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import { Button } from "./Button";

export function ChromeSkeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={clsx(
        "bg-[var(--color-surface-muted)] rounded-md animate-pulse motion-reduce:animate-none",
        className,
      )}
    />
  );
}

export function ChromeLoading({ label = "Loading…" }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="p-6 space-y-4">
      <span className="sr-only">{label}</span>
      <ChromeSkeleton className="h-7 w-1/3" />
      <ChromeSkeleton className="h-4 w-2/3" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
        <ChromeSkeleton className="h-24" />
        <ChromeSkeleton className="h-24" />
        <ChromeSkeleton className="h-24" />
      </div>
      <ChromeSkeleton className="h-64" />
    </div>
  );
}

export interface ChromeEmptyProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  minimal?: boolean;
}

export function ChromeEmpty({ icon, title, description, action, minimal }: ChromeEmptyProps) {
  if (minimal) {
    return (
      <div role="status" className="p-6 text-center">
        <p className="text-sm font-medium text-[var(--color-text-primary)]">{title}</p>
        {description && <p className="text-xs text-[var(--color-text-secondary)] mt-1">{description}</p>}
        {action && <div className="mt-3">{action}</div>}
      </div>
    );
  }
  return (
    <div role="status" className="flex flex-col items-center justify-center p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] flex items-center justify-center mb-4">
        {icon ?? <Sparkles size={28} aria-hidden />}
      </div>
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h2>
      {description && (
        <p className="text-sm text-[var(--color-text-secondary)] mt-1 max-w-md">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export interface ChromeErrorProps {
  error: Error;
  errorId?: string;
  onRetry?: () => void;
}

export function ChromeError({ error, errorId, onRetry }: ChromeErrorProps) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-[var(--color-danger-100)] text-[var(--color-danger-500)] flex items-center justify-center mb-4">
        <AlertTriangle size={28} aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Something went wrong</h2>
      <p className="text-sm text-[var(--color-text-secondary)] mt-1 max-w-md">
        {error.message || "An unexpected error occurred."}
      </p>
      <div className="flex gap-2 mt-4">
        {onRetry && <Button onClick={onRetry}>Retry</Button>}
        <Button
          variant="secondary"
          onClick={() => {
            const subject = encodeURIComponent(`Bug report${errorId ? ` — ${errorId}` : ""}`);
            const body = encodeURIComponent(`Error: ${error.message}\n\nStack:\n${error.stack ?? "(no stack)"}`);
            window.open(`mailto:support@perlab.demo?subject=${subject}&body=${body}`, "_blank");
          }}
        >
          Report
        </Button>
      </div>
      {errorId && (
        <p className="text-xs text-[var(--color-text-disabled)] mt-3 font-mono">Error ID: {errorId}</p>
      )}
    </div>
  );
}

function generateErrorId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ChromeErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
        <ChromeError error={error as Error} errorId={generateErrorId()} onRetry={resetErrorBoundary} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export interface ChromeForbiddenProps {
  appName?: string;
  description?: string;
  onRequestAccess?: () => void;
  onSwitchOrg?: () => void;
  // P0 v5.3 Task 13-sub: optional CTA label override (e.g., "Switch engagement").
  switchOrgLabel?: string;
}

export function ChromeForbidden({
  appName = "this app",
  description,
  onRequestAccess,
  onSwitchOrg,
  switchOrgLabel = "Switch workspace",
}: ChromeForbiddenProps) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] flex items-center justify-center mb-4">
        <Lock size={28} aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
        You don't have access to {appName}
      </h2>
      <p className="text-sm text-[var(--color-text-secondary)] mt-1 max-w-md">
        {description ?? "Ask a workspace admin to grant access, or switch to a workspace where you have permission."}
      </p>
      <div className="flex gap-2 mt-4">
        {onRequestAccess && <Button onClick={onRequestAccess}>Request access</Button>}
        {onSwitchOrg && (
          <Button variant="secondary" onClick={onSwitchOrg}>{switchOrgLabel}</Button>
        )}
      </div>
    </div>
  );
}
